import { Component, OnInit, ViewChild, AfterViewInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { GenericFormComponent } from '../../../../../shared/components/generic-crud/generic-form/generic-form.component';
import { GenericForm, GenericFormField } from '../../../../../models/GenericCRUD/GenericForm';

@Component({
  selector: 'app-send-email',
  standalone: true,
  imports: [
    CommonModule,
    GenericFormComponent,
  ],
  templateUrl: './send-email.component.html',
  styleUrl: './send-email.component.css',
})
export class SendEmailComponent implements OnInit, AfterViewInit {
  @ViewChild('formComponent') formComponent!: GenericFormComponent;

  genericForm!: GenericForm;
  templateSelected = false;

  // Constantes privadas
  private readonly SEND_TYPE_OPTIONS = [
    { id: 'individual', name: 'Email Individual' },
    { id: 'bulk', name: 'Email Masivo' }
  ] as const;

  private readonly FILTER_TYPE_OPTIONS = [
    { id: 'manual', name: 'Alumnos específicos (selección manual)' },
    { id: 'course_call', name: 'Todos los alumnos de una convocatoria de curso' },
    { id: 'proof', name: 'Todos los alumnos de una convocatoria de examen' },
    { id: 'product', name: 'Todos los alumnos con un producto contratado' }
  ] as const;

  constructor() {}

  ngOnInit() {
    this.initializeForm();
  }

  ngAfterViewInit() {
    if (this.formComponent && this.formComponent.formGroup) {
      // Suscribirse a los cambios del campo send_type
      this.formComponent.formGroup.get('send_type')?.valueChanges.subscribe(value => {
        // Manejar valores nulos después del reset del formulario
        if (!value) return;

        const sendType = typeof value === 'object' ? value.id : value;

        if (sendType === 'individual') {
          this.genericForm.createApiSlug = '/email/send-individual';
        } else if (sendType === 'bulk') {
          this.genericForm.createApiSlug = '/email/send-bulk';
        }
      });

      // Suscribirse a los cambios del campo template_id
      this.formComponent.formGroup.get('template_id')?.valueChanges.subscribe(value => {
        // Manejar valores nulos después del reset del formulario
        const templateId = value && typeof value === 'object' ? value?.id : value;
        this.templateSelected = !!templateId;
      });
    }
  }

  /**
   * Extrae el send_type del formData manejando tanto objetos como strings
   */
  private getSendType(formData: any): string | null {
    if (!formData?.send_type) return null;
    return typeof formData.send_type === 'object'
      ? formData.send_type.id
      : formData.send_type;
  }

  /**
   * Extrae el filter_type del formData manejando tanto objetos como strings
   */
  private getFilterType(formData: any): string | null {
    if (!formData?.filter_type) return null;
    return typeof formData.filter_type === 'object'
      ? formData.filter_type.id
      : formData.filter_type;
  }

  /**
   * Determina si el formulario es de tipo bulk
   */
  private isBulkType(formData: any): boolean {
    return this.getSendType(formData) === 'bulk';
  }

  /**
   * Determina si el formulario es de tipo individual
   */
  private isIndividualType(formData: any): boolean {
    return this.getSendType(formData) === 'individual';
  }

  /**
   * Crea un campo select condicional basado en el filter_type
   */
  private createConditionalSelectField(
    name: string,
    label: string,
    url: string,
    labelKey: string,
    secondaryLabelKey: string,
    filterTypeCondition: string,
    singleSelection: boolean = true
  ): GenericFormField {
    return {
      name,
      type: 'select',
      label,
      validations: '',
      url,
      labelKey,
      secondaryLabelKey,
      singleSelection,
      colspan: 2,
      showIf: (formData: any) =>
        this.isBulkType(formData) && this.getFilterType(formData) === filterTypeCondition
    };
  }

  initializeForm() {
    const formFields: GenericFormField[] = [
      // Tipo de envío
      {
        name: 'send_type',
        type: 'select',
        label: 'Tipo de envío',
        validations: 'required',
        optionsList: [...this.SEND_TYPE_OPTIONS],
        labelKey: 'name',
        valueKey: 'id',
        singleSelection: true,
        colspan: 2
      },
      // INDIVIDUAL: Alumno único
      {
        name: 'recipient_id',
        type: 'select',
        label: 'Alumno destinatario',
        validations: '',
        url: '/user/students',
        labelKey: 'name',
        secondaryLabelKey: 'email',
        singleSelection: true,
        colspan: 2,
        showIf: (formData: any) => this.isIndividualType(formData)
      },
      // BULK: Filtro de destinatarios
      {
        name: 'filter_type',
        type: 'select',
        label: 'Enviar email a',
        validations: '',
        optionsList: [...this.FILTER_TYPE_OPTIONS],
        labelKey: 'name',
        valueKey: 'id',
        singleSelection: true,
        colspan: 2,
        showIf: (formData: any) => this.isBulkType(formData)
      },
      // BULK: Campos condicionales según filter_type
      this.createConditionalSelectField(
        'recipient_ids',
        'Selecciona los alumnos',
        '/user/students',
        'name',
        'email',
        'manual',
        false
      ),
      this.createConditionalSelectField(
        'course_call_id',
        'Selecciona la convocatoria de curso',
        '/course/index?paginate=false',
        'name',
        'course_definition_name',
        'course_call'
      ),
      this.createConditionalSelectField(
        'proof_id',
        'Selecciona la convocatoria de examen',
        '/proof/index/999?paginate=false',
        'name',
        'generated_name',
        'proof'
      ),
      this.createConditionalSelectField(
        'product_id',
        'Selecciona el producto',
        '/product/listAll',
        'name',
        '',
        'product'
      ),
      // Plantilla de email (opcional)
      {
        name: 'template_id',
        type: 'select',
        label: 'Plantilla de Email',
        validations: '',
        url: '/email-template/active',
        labelKey: 'name',
        secondaryLabelKey: 'subject',
        singleSelection: true,
        colspan: 2
      },
      // Campos comunes: asunto y cuerpo (opcionales si se selecciona plantilla)
      {
        name: 'subject',
        type: 'text',
        label: 'Asunto del email',
        validations: '',
        colspan: 2,
        showIf: (formData: any) => !this.hasTemplateSelected(formData)
      },
      {
        name: 'body',
        type: 'textarea',
        label: 'Cuerpo del email',
        validations: '',
        colspan: 2,
        showIf: (formData: any) => !this.hasTemplateSelected(formData)
      }
    ];

    this.genericForm = {
      fields: formFields,
      method: 'POST',
      name: 'send-email',
      successTitle: 'Email enviado',
      errorTitle: 'Error',
      width: '2xl',
      formTitle: 'Enviar Email',
      formButtonTitle: 'Enviar Email',
      createButtonTitle: 'Enviar',
      createApiSlug: '/email/send-individual', // Valor por defecto
      cols: 2
    };
  }


  /**
   * Determina si hay una plantilla seleccionada en el formulario
   */
  hasTemplateSelected(formData: any): boolean {
    if (!formData?.template_id) return false;
    const templateId = typeof formData.template_id === 'object'
      ? formData.template_id?.id
      : formData.template_id;
    return !!templateId;
  }

  onFormFinished(result: any) {
    // Resetear el formulario para dar sensación de envío exitoso
    if (this.formComponent && this.formComponent.formGroup) {
      this.formComponent.formGroup.reset();
      this.templateSelected = false;
    }
  }
}
